import { gql } from "@apollo/client";
import { builder } from "utils/graphql";
import BaseService from "services/base";
import { get } from "utils/lodash";
import { BOOKING_IN_SHOP_SELECTION, BOOKING_SELECTION } from "./constant";
import {
  BookingDetail,
  BookingStatus,
  PaymentMethod,
  PaymentStatus,
} from "types/model/booking";
import { BOOKING_API_URL } from "./constant";

// Import GraphQL queries directly
import {
  QUERY_BOOKING_IN_SHOP,
  BOOKING_COUNT_BY_DATE_RANGE,
  COUNT_BOOKINGS
} from "graphql/queries/booking";

class BookingService extends BaseService {
  name = "Booking";
  static override DEFINITION = builder.createDefinition({
    name: "Booking",
    baseQuery: "Booking",
    baseSelection: `
        ${BOOKING_SELECTION}
      `,
    GQL_SHAPE_QUERY: {
      BASE: `
        {{ selections }}
      `,
      FIND: `
        docs {
          {{ selections }}
        }
      `,
    },
    GQL_ACTIONS: {
      FIND: "Bookings",
    },
    GQL_RESPONSE_DATA_PATH: {
      BASE: "data.Booking",
      FIND: "data.Bookings.docs",
      PARAMS: "data.Bookings",
    },
  });
  cancelBooking(options: object = {}, selections: string = "") {
    const nameQuery = "updateBooking";
    const dataType = "mutationBookingUpdateInput!";

    const customSelection = selections || BOOKING_SELECTION;
    return this.custom(
      {
        typeQL: "mutation",
        PATH_RESPONSE: {
          DATA: `data.${nameQuery}`,
          PARAMS: "",
        },
        variables: {
          id: get(options, "id"),
          data: {
            status: "shop_cancelled",
          },
        },
      },
      `mutation ${nameQuery}($id: String!, $data: ${dataType}) {
        ${nameQuery} (id: $id, data: $data) {
          ${customSelection}
        }
      }`
    );
  }
  notShowBooking(options: object = {}, selections: string = "") {
    const nameQuery = "updateBooking";
    const dataType = "mutationBookingUpdateInput!";

    const customSelection = selections || BOOKING_SELECTION;
    return this.custom(
      {
        typeQL: "mutation",
        PATH_RESPONSE: {
          DATA: `data.${nameQuery}`,
          PARAMS: "",
        },
        variables: {
          id: get(options, "id"),
          data: {
            status: "not_show",
          },
        },
      },
      `mutation ${nameQuery}($id: String!, $data: ${dataType}) {
        ${nameQuery} (id: $id, data: $data) {
          ${customSelection}
        }
      }`
    );
  }
  doneBooking(options: object = {}, selections: string = "") {
    const nameQuery = "updateBooking";
    const dataType = "mutationBookingUpdateInput!";

    const customSelection = selections || BOOKING_SELECTION;
    return this.custom(
      {
        typeQL: "mutation",
        PATH_RESPONSE: {
          DATA: `data.${nameQuery}`,
          PARAMS: "",
        },
        variables: {
          id: get(options, "id"),
          data: {
            status: "done",
          },
        },
      },
      `mutation ${nameQuery}($id: String!, $data: ${dataType}) {
        ${nameQuery} (id: $id, data: $data) {
          ${customSelection}
        }
      }`
    );
  }
  acceptedBooking(options: object = {}, selections: string = "") {
    const nameQuery = "updateBooking";
    const dataType = "mutationBookingUpdateInput!";

    const customSelection = selections || BOOKING_SELECTION;
    return this.custom(
      {
        typeQL: "mutation",
        PATH_RESPONSE: {
          DATA: `data.${nameQuery}`,
          PARAMS: "",
        },
        variables: {
          id: get(options, "id"),
          data: {
            status: "accepted",
          },
        },
      },
      `mutation ${nameQuery}($id: String!, $data: ${dataType}) {
        ${nameQuery} (id: $id, data: $data) {
          ${customSelection}
        }
      }`
    );
  }
  async bookingCountByDateRange(options: object = {}, selections: string = "") {
    const nameQuery = "bookingCountByDateRange";
    const inputType = "BookingCountByDateInput!";
    const customSelection = selections;
    return this.custom(
      {
        typeQL: "query",
        PATH_RESPONSE: {
          DATA: "data.bookingCountByDateRange",
          PARAMS: "",
        },
        ...options,
      },
      `query ${nameQuery}($input: ${inputType}) {
        ${nameQuery} (input: $input) {
          ${customSelection}
        }
      }`
    );
  }
  async queryBookingInShop(options: object = {}, selections: string = "") {
    const nameQuery = "queryBookingInShop";
    const customSelection = selections;
    
    // Use centralized query when no custom selection is provided
    if (!selections) {
      return this.custom(
        {
          typeQL: "query",
          PATH_RESPONSE: {
            DATA: "data.queryBookingInShop.bookings",
            PARAMS: "data.queryBookingInShop.queryParams",
          },
          ...options,
        },
        QUERY_BOOKING_IN_SHOP
      );
    }
    
    // Fall back to dynamic query with custom selection
    const inputType = "QueryBookingInShopInput!";
    return this.custom(
      {
        typeQL: "query",
        PATH_RESPONSE: {
          DATA: "data.queryBookingInShop.bookings",
          PARAMS: "data.queryBookingInShop.queryParams",
        },
        ...options,
      },
      `query ${nameQuery}($input: ${inputType}) {
        ${nameQuery} (input: $input) {
          bookings {
            ${customSelection}
          }
          queryParams {
            total
          }
        }
      }`
    );
  }
  async countBookings(options: object = {}) {
    const nameQuery = "countBookings";
    return this.custom(
      {
        typeQL: "query",
        PATH_RESPONSE: {
          DATA: "data.countBookings",
          PARAMS: "",
        },
        ...options,
      },
      `query ${nameQuery}($where: Booking_where) {
          ${nameQuery} (where: $where) {
            totalDocs
          }
        }`
    );
  }
}

const Instance = new BookingService();
export default Instance;
